import { mxCell, mxGraph } from 'mxgraph-js';
import FaultTreeMxGraph, { FaultTreeMxGraphJSON } from './FaultTreeMxGraph';
import { OutcomeJSON } from '../HCLTreeNode/HCLTreeVertexValue/Outcome';
import ReferenceTypes from '../HCLTreeNode/HCLTreeVertexValue/ReferenceTypes';
import { GateVertexValueJSON } from '../HCLTreeNode/FaultTreeNode/GateNode/GateVertexValue';
import ProxyTypes from '../HCLTreeNode/HCLTreeVertexValue/ProxyTypes';
import { FormulaJSON } from '../HCLTreeNode/HCLTreeVertexValue/Formula';
import FaultTreeManagementMxStylesheet from '../Stylesheet/FaultTreeManagementMxStylesheet';
import { OverviewTreeData } from '../../components/Model/SingleHCLModelView/OverviewTreeViewer';
import BasicEventVertexValue, {BasicEventVertexValueJSON} from "../HCLTreeNode/FaultTreeNode/BasicEventNode/BasicEventVertexValue";
import Label from "../HCLTreeNode/HCLTreeVertexValue/Label";
import Expression from "../HCLTreeNode/HCLTreeVertexValue/Expression";
import BasicEventVertex from "../HCLTreeNode/FaultTreeNode/BasicEventNode/BasicEventVertex";
import GateVertexBuilder from "../HCLTreeNode/FaultTreeNode/GateNode/GateVertexBuilder";
import GateVertex from "../HCLTreeNode/FaultTreeNode/GateNode/GateVertex";

const fillColorMaps = {
  PR: 'rgba(222,130,125,0.2)',
  SU: 'rgba(148,227,196,0.2)',
  CO: 'rgba(151,145,68,0.2)',
};

const borderColorMaps = {
  PR: 'rgba(140,84,46,0.65)',
  SU: 'rgba(73,151,140,0.65)',
  CO: 'rgba(151,149,85,0.65)',
};

class FaultTreeManagementMxGraph extends FaultTreeMxGraph {
  private rootLabel: string;
  private showBasicEvents: boolean;
  private showLogicalGates: boolean;
  private [ReferenceTypes.GATES]: {[key: string]: mxCell};
  private [ReferenceTypes.BASIC_EVENTS]: {[key: string]: mxCell};
  private levels: any[];
  private levelsToShow?: number | null;

  constructor(onGraphMounted: () => void, onGraphUnmounted: () => void, rootLabel: string, showLogicalGates: boolean, showBasicEvents: boolean, levelsToShow?: number) {
    super(onGraphMounted, onGraphUnmounted);
    this.rootLabel = rootLabel;
    this.showBasicEvents = showBasicEvents;
    this.showLogicalGates = showLogicalGates;
    this.setCellsMovable(false);
    this.setAutoSizeCells(true);
    this.setCellsSelectable(true);
    this.setHtmlLabels(true);
    this.setIDsVisible(false);
    this.gates = {};
    this.basic_events = {};
    this.levels = [];
    this.levelsToShow = levelsToShow ? levelsToShow : null;
  }

  public decode(data: OverviewTreeData) {
    const treeData = data[data.overview_tree_id];
    if (!treeData || !Object.entries(treeData).length) {
      return;
    }
    const root = this.insertVertex(this.getDefaultParent(), null, this.rootLabel, 0, 0, 120, 30, `fillColor=${fillColorMaps[treeData.model.model_tag as 'PR' | 'SU' | 'CO']};strokeColor=${borderColorMaps[treeData.model.model_tag as 'PR' | 'SU' | 'CO']}`);
    // @ts-ignore
    root.model_id = treeData.model.id;
    // @ts-ignore
    root.tree_id = treeData.id;
    // @ts-ignore
    root.tag = treeData.model.model_tag;
    this.updateCellSize(root, true);
    this.decodeHelper(data, treeData as FaultTreeMxGraphJSON, (treeData as FaultTreeMxGraphJSON).top_node, root);
    // Remove corresponding cells if user limits nesting
    if (this.levelsToShow && this.levels[this.levelsToShow - 1]) {
      this.levels.map((arr, idx) => {
        if (idx > (this.levelsToShow - 1)) this.levels[idx].forEach(vertex => this.removeCells([vertex]))
      })
    }
  }

  private insertUniqueEdge(source: mxCell, target: mxCell) {
    if (!source.edges || source.edges.filter(e => e.target === target).length === 0) {
      return this.insertEdge(this.getDefaultParent(), null, null, source, target);
    }
    return null;
  }

  private decodeHelper(data: OverviewTreeData, treeData: FaultTreeMxGraphJSON, topNodeReferenceJSON: OutcomeJSON, parent: mxCell) {
    if (!topNodeReferenceJSON) return null;

    const topNodeJSON = treeData
      [topNodeReferenceJSON.reference_type as ReferenceTypes.BASIC_EVENTS | ReferenceTypes.HOUSE_EVENTS | ReferenceTypes.GATES]
      [topNodeReferenceJSON.name];

    if (!topNodeJSON) throw new Error(`Failed to find vertex value JSON from vertex reference ${JSON.stringify(topNodeReferenceJSON)}`);

    switch (topNodeReferenceJSON.reference_type) {
      case ReferenceTypes.GATES:
        const gateVertexValueJSON = topNodeJSON as GateVertexValueJSON;
        // Transfer Gate
        if (gateVertexValueJSON.formula._proxy === ProxyTypes.EVENT_REFERENCE) {
          const outcomeJSON = gateVertexValueJSON.formula as OutcomeJSON;

          const UID = topNodeReferenceJSON.name + topNodeReferenceJSON.tree_id + parent.id;

          if (!this.gates[UID]) {
            const newData = data[outcomeJSON.tree_id];
            if (newData && Object.entries(newData).length) {
              // this.gates[UID] = this.addCell(new MxGraph.mxCell(gateVertexValueJSON.label.name, new MxGraph.mxGeometry(0, 0, 120, 30)));
              this.gates[UID] = this.insertVertex(this.getDefaultParent(), null, gateVertexValueJSON.label.name, 0, 0, 120, 30, `fillColor=${fillColorMaps[newData.model.model_tag as 'PR' | 'SU' | 'CO']};strokeColor=${borderColorMaps[newData.model.model_tag as 'PR' | 'SU' | 'CO']}`);
              // @ts-ignore
              this.gates[UID].model_id = newData.model.id;
              // @ts-ignore
              this.gates[UID].tree_id = newData.id;
              // @ts-ignore
              this.gates[UID].tag = newData.model.model_tag;

              // Populate graph nesting levels
              // Is Nested Level 1
              if (this.getDefaultParent().children[0] === parent) {
                // If Level 1 has been instantiated
                if (this.levels[0]) this.levels[0].push(this.gates[UID]);
                else this.levels.push([this.gates[UID]]);
              } else {
                // Is Nested Level 2+
                const parentLevelIndex = this.levels.findIndex((level: any[]) => level.includes(parent));
                if (this.levels[parentLevelIndex + 1]) this.levels[parentLevelIndex + 1].push(this.gates[UID])
                else this.levels.push([this.gates[UID]]);
              }

              const child = this.decodeHelper(data, data[outcomeJSON.tree_id] as FaultTreeMxGraphJSON, outcomeJSON, this.gates[UID]);
              this.insertUniqueEdge(this.gates[UID], child);
              this.updateCellSize(this.gates[UID], true);
            }
          }

          return this.gates[UID];
        }
        // Logical expression
        if (gateVertexValueJSON.formula._proxy === ProxyTypes.LOGICAL_EXPRESSION && this.showLogicalGates) {
          const outcomeJSON = gateVertexValueJSON.formula as GateVertexValueJSON;
          let UID = topNodeReferenceJSON.name + topNodeReferenceJSON.tree_id + parent.id;
          if (!this.gates[UID]) {
            this.gates[UID] = this.addVertex(GateVertexBuilder.build(UID, gateVertexValueJSON)) as GateVertex;
            // @ts-ignore
            this.gates[UID].model_id = treeData.model.id;
            // @ts-ignore
            this.gates[UID].tree_id = treeData.id;
            // @ts-ignore
            this.gates[UID].tag = treeData.model.model_tag;

            // Populate graph nesting levels
            // Is Nested Level 1
            if (this.getDefaultParent().children[0] === parent) {
              // If Level 1 has been instantiated
              if (this.levels[0]) this.levels[0].push(this.gates[UID]);
              else this.levels.push([this.gates[UID]]);
            } else {
              // Is Nested Level 2+
              const parentLevelIndex = this.levels.findIndex((level: any[]) => level.includes(parent));
              if (this.levels[parentLevelIndex + 1]) this.levels[parentLevelIndex + 1].push(this.gates[UID])
              else this.levels.push([this.gates[UID]]);
            }
            for (const outcomeJSON of (gateVertexValueJSON.formula as FormulaJSON).formulas || []) {
              const child = this.decodeHelper(data, treeData, outcomeJSON, this.gates[UID]);
              if (this.getDefaultParent().children[0] === parent) this.insertUniqueEdge(parent, this.gates[UID]);
              this.insertUniqueEdge(this.gates[UID], child);
            }
          }

          return this.gates[UID];
        }

        for (const outcomeJSON of (gateVertexValueJSON.formula as FormulaJSON).formulas || []) {
          const child = this.decodeHelper(data, treeData, outcomeJSON, parent);
          this.insertUniqueEdge(parent, child);
        }

        break;
      case ReferenceTypes.BASIC_EVENTS:
        if (!this.showBasicEvents) return null;

        // Init basic event data
        const beVertexValueJSON = topNodeJSON as BasicEventVertexValueJSON;
        const key = topNodeReferenceJSON.name + topNodeReferenceJSON.tree_id + parent.id;
        let value = new BasicEventVertexValue(new Label(key), new Expression(beVertexValueJSON.expression));
        if (beVertexValueJSON.label && Object.keys(beVertexValueJSON.label).length) value = new BasicEventVertexValue(Label.build(beVertexValueJSON.label), new Expression(beVertexValueJSON.expression));

        // Add basic event vertex
        this.basic_events[key] = this.addVertex(new BasicEventVertex(key, value)) as BasicEventVertex;
        this.insertUniqueEdge(parent, this.basic_events[key]);

        // Populate "levels" for user nesting level control
        const parentLevelIndex = this.levels.findIndex((level: any[]) => level.includes(parent));
        if (this.levels[parentLevelIndex + 1]) this.levels[parentLevelIndex + 1].push(this.basic_events[key]);
        else this.levels.push([this.basic_events[key]]);
      default:
        break;
    }
  }

  createStylesheet(): FaultTreeManagementMxStylesheet {
    return new FaultTreeManagementMxStylesheet();
  }
}

export default FaultTreeManagementMxGraph;
